import React from "react";
import {
  MDBIcon,
  MDBSpinner,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBInput,
} from "mdb-react-ui-kit";
import {
  getLibraries,
  createLibrary,
  updateLibrary,
  deleteLibrary,
} from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import "./LibraryTree.css";

/**
 * LibraryTree — Sidebar de navegación de bibliotecas MFM con CRUD.
 *
 * Props:
 *   selected          { type, appCode?, idLibrary?, name?, slug? } | null
 *   onSelect          (selection) => void
 *   refreshKey        number — incrementar para forzar recarga
 *   onLibraryChanged  () => void — llamado tras crear/editar/eliminar
 */
export default class LibraryTree extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      libraries: [],
      grouped: {},
      collapsedApps: {},
      loading: true,
      error: null,
      // Modal crear/editar
      modal: { open: false, mode: "create", appCode: null, lib: null },
      modalName: "",
      modalDesc: "",
      saving: false,
      // Confirmación eliminar
      deleteTarget: null,   // { idLibrary, name }
      deleting: false,
    };
  }

  componentDidMount() {
    this.loadLibraries();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.refreshKey !== this.props.refreshKey) {
      this.loadLibraries();
    }
  }

  loadLibraries = async () => {
    this.setState({ loading: true, error: null });
    try {
      const data = await getLibraries();
      const grouped = this.groupByApp(data);
      const collapsedApps = {};
      Object.keys(grouped).forEach((app) => { collapsedApps[app] = false; });
      this.setState({ libraries: data, grouped, collapsedApps });
    } catch (_) {
      this.setState({ error: "Error al cargar bibliotecas." });
    } finally {
      this.setState({ loading: false });
    }
  };

  groupByApp = (libs) =>
    libs.reduce((acc, lib) => {
      const key = lib.appCode;
      if (!acc[key]) acc[key] = { libs: [], appName: lib.appName || lib.appCode };
      acc[key].libs.push(lib);
      return acc;
    }, {});

  totalCount = () => this.state.libraries.reduce((s, l) => s + (l.fileCount || 0), 0);
  appCount   = (app) => (this.state.grouped[app]?.libs || []).reduce((s, l) => s + (l.fileCount || 0), 0);

  toggleApp = (appCode) => {
    this.setState((prev) => ({
      collapsedApps: { ...prev.collapsedApps, [appCode]: !prev.collapsedApps[appCode] },
    }));
  };

  isSelected = (type, appCode, idLibrary) => {
    const { selected } = this.props;
    if (!selected) return type === "all";
    if (type === "all")     return selected.type === "all";
    if (type === "app")     return selected.type === "app" && selected.appCode === appCode;
    if (type === "library") return selected.type === "library" && selected.idLibrary === idLibrary;
    return false;
  };

  selectAll     = () => this.props.onSelect({ type: "all" });
  selectApp     = (appCode) => this.props.onSelect({ type: "app", appCode });
  selectLibrary = (lib) =>
    this.props.onSelect({ type: "library", idLibrary: lib.idLibrary, appCode: lib.appCode, name: lib.name, slug: lib.slug });

  // ── CRUD ──────────────────────────────────────────────
  openCreate = (e, appCode) => {
    e.stopPropagation();
    this.setState({ modal: { open: true, mode: "create", appCode, lib: null }, modalName: "", modalDesc: "" });
  };

  openEdit = (e, lib) => {
    e.stopPropagation();
    this.setState({ modal: { open: true, mode: "edit", appCode: lib.appCode, lib }, modalName: lib.name, modalDesc: lib.description || "" });
  };

  closeModal = () => this.setState({ modal: { open: false, mode: "create", appCode: null, lib: null } });

  saveModal = async () => {
    const { modal, modalName, modalDesc } = this.state;
    if (!modalName.trim()) return;
    this.setState({ saving: true });
    try {
      if (modal.mode === "create") {
        await createLibrary(modal.appCode, modalName.trim(), modalDesc.trim());
        toast.show({ message: "Biblioteca creada", type: "success", position: "bottom-center", size: "narrow", timeout: 3000 });
      } else {
        await updateLibrary(modal.lib.idLibrary, modalName.trim(), modalDesc.trim());
        toast.show({ message: "Biblioteca actualizada", type: "success", position: "bottom-center", size: "narrow", timeout: 3000 });
      }
      this.closeModal();
      await this.loadLibraries();
      this.props.onLibraryChanged?.();
    } catch (err) {
      toast.show({ message: err.message || "Error al guardar", type: "error", position: "bottom-center", size: "narrow", timeout: 4000 });
    } finally {
      this.setState({ saving: false });
    }
  };

  confirmDelete = (e, lib) => {
    e.stopPropagation();
    this.setState({ deleteTarget: { idLibrary: lib.idLibrary, name: lib.name } });
  };

  cancelDelete = () => this.setState({ deleteTarget: null });

  doDelete = async () => {
    const { deleteTarget } = this.state;
    if (!deleteTarget) return;
    this.setState({ deleting: true });
    try {
      await deleteLibrary(deleteTarget.idLibrary);
      toast.show({ message: "Biblioteca eliminada", type: "info", position: "bottom-center", size: "narrow", timeout: 3000 });
      this.setState({ deleteTarget: null });
      await this.loadLibraries();
      this.props.onLibraryChanged?.();
    } catch (err) {
      toast.show({ message: err.message || "Error al eliminar", type: "error", position: "bottom-center", size: "narrow", timeout: 4000 });
    } finally {
      this.setState({ deleting: false });
    }
  };

  // ── RENDER ────────────────────────────────────────────
  render() {
    const { libraries, grouped, collapsedApps, loading, error,
            modal, modalName, modalDesc, saving, deleteTarget, deleting } = this.state;

    if (loading) return <div className="lib-tree-loading"><MDBSpinner size="sm" color="primary" /></div>;
    if (error)   return <div className="lib-tree-error"><MDBIcon fas icon="triangle-exclamation" className="me-1" />{error}</div>;

    const appCodes = Object.keys(grouped).sort();

    return (
      <div className="lib-tree">

        {/* ── Todas ────────────────────────────── */}
        <div
          className={`lib-tree-all${this.isSelected("all") ? " lib-tree-item--active" : ""}`}
          onClick={this.selectAll}
          role="button" tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && this.selectAll()}
        >
          <MDBIcon fas icon="layer-group" className="lib-tree-icon" />
          <span className="lib-tree-label">Todas</span>
          <span className="lib-tree-count">{this.totalCount()}</span>
        </div>

        {/* ── Apps ─────────────────────────────── */}
        {appCodes.map((appCode) => {
          const group = grouped[appCode];
          const libs = group.libs;
          const appName = group.appName;
          const isCollapsed = collapsedApps[appCode];

          return (
            <div key={appCode} className="lib-tree-app">
              <div className={`lib-tree-app-header${this.isSelected("app", appCode) ? " lib-tree-item--active" : ""}`}>
                <span
                  className="lib-tree-app-toggle"
                  onClick={() => this.toggleApp(appCode)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && this.toggleApp(appCode)}
                >
                  <MDBIcon fas icon={isCollapsed ? "chevron-right" : "chevron-down"} className="lib-tree-chevron" />
                </span>
                <span
                  className="lib-tree-app-label"
                  onClick={() => this.selectApp(appCode)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && this.selectApp(appCode)}
                >
                  <MDBIcon fas icon="folder" className="lib-tree-icon lib-tree-icon--app" />
                  <span className="lib-tree-label" title={appCode}>{appName}</span>
                  <span className="lib-tree-count">{this.appCount(appCode)}</span>
                </span>
                <button
                  className="lib-tree-crud-btn lib-tree-crud-btn--add"
                  title="Nueva biblioteca"
                  onClick={(e) => this.openCreate(e, appCode)}
                >
                  <MDBIcon fas icon="plus" />
                </button>
              </div>

              {!isCollapsed && (
                <div className="lib-tree-libs">
                  {libs.map((lib) => (
                    <div
                      key={lib.idLibrary}
                      className={`lib-tree-lib${this.isSelected("library", lib.appCode, lib.idLibrary) ? " lib-tree-item--active" : ""}`}
                      onClick={() => this.selectLibrary(lib)}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && this.selectLibrary(lib)}
                    >
                      <MDBIcon fas icon="folder-open" className="lib-tree-icon lib-tree-icon--lib" />
                      <span className="lib-tree-label" title={lib.description || lib.name}>{lib.name}</span>
                      <span className="lib-tree-count">{lib.fileCount || 0}</span>
                      <span className="lib-tree-lib-actions">
                        <button
                          className="lib-tree-crud-btn"
                          title="Editar biblioteca"
                          onClick={(e) => this.openEdit(e, lib)}
                        ><MDBIcon fas icon="pen" /></button>
                        <button
                          className="lib-tree-crud-btn lib-tree-crud-btn--del"
                          title="Eliminar biblioteca"
                          onClick={(e) => this.confirmDelete(e, lib)}
                        ><MDBIcon fas icon="trash" /></button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {libraries.length === 0 && (
          <p className="lib-tree-empty">Sin bibliotecas asignadas.</p>
        )}

        {/* ── Modal crear/editar ─────────────────────── */}
        <MDBModal open={modal.open} onClose={this.closeModal} tabIndex="-1">
          <MDBModalDialog size="sm">
            <MDBModalContent>
              <MDBModalHeader>
                <h5 className="modal-title">
                  {modal.mode === "create" ? `Nueva biblioteca — ${modal.appCode}` : "Editar biblioteca"}
                </h5>
                <MDBBtn className="btn-close" color="none" onClick={this.closeModal} />
              </MDBModalHeader>
              <MDBModalBody>
                <MDBInput
                  label="Nombre *"
                  value={modalName}
                  onChange={(e) => this.setState({ modalName: e.target.value })}
                  className="mb-3"
                />
                <MDBInput
                  label="Descripción"
                  value={modalDesc}
                  onChange={(e) => this.setState({ modalDesc: e.target.value })}
                />
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" size="sm" onClick={this.closeModal} disabled={saving}>Cancelar</MDBBtn>
                <MDBBtn color="primary" size="sm" onClick={this.saveModal} disabled={saving || !modalName.trim()}>
                  {saving ? <MDBSpinner size="sm" className="me-1" /> : null}
                  {modal.mode === "create" ? "Crear" : "Guardar"}
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* ── Modal confirmar eliminar ──────────────────── */}
        <MDBModal open={!!deleteTarget} onClose={this.cancelDelete} tabIndex="-1">
          <MDBModalDialog size="sm">
            <MDBModalContent>
              <MDBModalHeader>
                <h5 className="modal-title">Eliminar biblioteca</h5>
                <MDBBtn className="btn-close" color="none" onClick={this.cancelDelete} />
              </MDBModalHeader>
              <MDBModalBody>
                ¿Eliminar <strong>{deleteTarget?.name}</strong>? Los ficheros asociados <strong>no</strong> se borrarán del disco.
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" size="sm" onClick={this.cancelDelete} disabled={deleting}>Cancelar</MDBBtn>
                <MDBBtn color="danger" size="sm" onClick={this.doDelete} disabled={deleting}>
                  {deleting ? <MDBSpinner size="sm" className="me-1" /> : null}
                  Eliminar
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

      </div>
    );
  }
}
